import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, FlaskConical, FileText, BookOpen, LineChart, UtensilsCrossed, TrendingUp, Library } from "lucide-react";
import { SEO } from "../components/SEO";
import { PageHero } from "../components/PageHero";
import { Reveal, Eyebrow } from "../components/primitives";
import { IMAGES, LOGO_MARK } from "../lib/site";

const FLOW = ["Measure", "Understand", "Improve", "Save", "Report", "Scale"];

const COMING_SOON = [
    { icon: FlaskConical, title: "Research Projects" },
    { icon: FileText, title: "Reports" },
    { icon: BookOpen, title: "Case Studies" },
    { icon: LineChart, title: "Hospitality Sustainability Insights" },
    { icon: UtensilsCrossed, title: "Food-Waste Research" },
    { icon: TrendingUp, title: "Sustainability Trends" },
    { icon: Library, title: "Future Publications" },
];

const Research = () => (
    <main>
        <SEO
            title="Research & Insights | Global Green Hotel"
            description="GGH's current research focus: hotel food waste and financial sustainability — evidence-based approaches to sustainable hotel operations."
        />
        <PageHero
            testid="research-hero"
            eyebrow="Research & Insights"
            titleLines={["Evidence before", "bold claims"]}
            description="GGH is interested in evidence-based approaches to sustainable hotel operations — research that hotels can actually use."
        />

        {/* Current focus */}
        <section data-testid="research-focus" className="py-24 sm:py-32">
            <div className="mx-auto grid max-w-[1440px] items-center gap-14 px-5 sm:px-8 lg:grid-cols-2 lg:gap-20 lg:px-12">
                <div>
                    <Reveal>
                        <Eyebrow>Current Research Focus</Eyebrow>
                        <h2 className="font-display mt-4 text-3xl font-medium tracking-tight text-[#0A1711] sm:text-4xl">
                            Hotel Food Waste & Financial Sustainability
                        </h2>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <p className="mt-7 text-base leading-relaxed text-[#4A5D54] md:text-lg">
                            GGH's initial research and development focus is{" "}
                            <strong className="font-semibold text-[#0A1711]">
                                Reducing Food Waste as a Business Strategy
                            </strong>
                            . The project explores how hotels can reduce food waste while
                            improving operational and financial performance.
                        </p>
                        <p className="font-display mt-6 border-l-2 border-[#C2A160] pl-5 text-lg italic leading-relaxed text-[#1A3B2B]">
                            “Reducing Food Waste as a Business Strategy: Developing a Financially
                            Sustainable Food-Waste Management Model for Hotels in Hawaiʻi.”
                        </p>
                    </Reveal>
                    <Reveal delay={0.25}>
                        <ol className="mt-8 flex flex-wrap items-center gap-2" data-testid="research-flow">
                            {FLOW.map((step, i) => (
                                <li key={step} className="flex items-center gap-2">
                                    <span className="rounded-full border border-[#1A3B2B]/25 bg-[#1A3B2B]/5 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-[#1A3B2B]">
                                        {step}
                                    </span>
                                    {i < FLOW.length - 1 && (
                                        <ArrowRight size={14} className="text-[#C2A160]" aria-hidden="true" />
                                    )}
                                </li>
                            ))}
                        </ol>
                    </Reveal>
                    <Reveal delay={0.35}>
                        <Link to="/contact" data-testid="cta-research-contact" className="btn-green mt-10">
                            Discuss Research Collaboration
                            <ArrowUpRight size={16} aria-hidden="true" />
                        </Link>
                    </Reveal>
                </div>
                <Reveal delay={0.2} className="relative">
                    <div className="overflow-hidden rounded-2xl border border-[#E8E6E1] shadow-[0_30px_80px_rgba(10,23,17,0.14)]">
                        <img
                            src={IMAGES.hawaii}
                            alt="The green mountains of Kauaʻi, Hawaiʻi — home of GGH's first research focus"
                            className="aspect-[4/3] w-full object-cover transition-transform duration-700 hover:scale-[1.03]"
                            loading="lazy"
                        />
                    </div>
                    <div aria-hidden="true" className="absolute -bottom-5 -left-5 -z-[1] h-full w-full rounded-2xl border border-[#C2A160]/40" />
                </Reveal>
            </div>
        </section>

        {/* Featured publication */}
        <section data-testid="research-featured" className="bg-[#F2EFE7] py-24 sm:py-32">
            <div className="mx-auto grid max-w-[1440px] items-center gap-14 px-5 sm:px-8 lg:grid-cols-2 lg:gap-20 lg:px-12">
                <div>
                    <Reveal>
                        <Eyebrow>Featured Publication</Eyebrow>
                        <h2 className="font-display mt-4 text-3xl font-medium tracking-tight text-[#0A1711] sm:text-4xl">
                            Global Green Hotel — Organization Profile
                        </h2>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <p className="mt-7 text-base leading-relaxed text-[#4A5D54] md:text-lg">
                            What GGH is, why it exists, how it creates value, and what makes it
                            different — the complete introduction to the initiative, including the
                            GGH at a Glance summary, the value-creation cycle, and the principles
                            guiding our work.
                        </p>
                    </Reveal>
                    <Reveal delay={0.28}>
                        <div className="mt-9 flex flex-wrap gap-4">
                            <Link to="/research/organization-profile" data-testid="cta-read-profile" className="btn-green">
                                Read the Profile
                                <ArrowUpRight size={16} aria-hidden="true" />
                            </Link>
                            <a
                                href="/assets/ggh-organization-profile.pdf"
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="cta-download-profile"
                                className="btn-outline-green"
                            >
                                Download PDF
                            </a>
                        </div>
                    </Reveal>
                </div>
                <Reveal delay={0.2} className="relative">
                    <Link
                        to="/research/organization-profile"
                        aria-label="Read the GGH Organization Profile"
                        className="card-lift grain relative block overflow-hidden rounded-2xl bg-[#04150D] p-10 sm:p-12"
                    >
                        <div className="relative z-[2]">
                            <img
                                src={LOGO_MARK}
                                alt=""
                                aria-hidden="true"
                                className="w-36 drop-shadow-[0_0_26px_rgba(194,161,96,0.25)]"
                                loading="lazy"
                            />
                            <p className="eyebrow-gold mt-10">Organization Profile</p>
                            <p className="font-display mt-3 text-2xl font-medium leading-snug text-[#FDFBF7] sm:text-3xl">
                                A sustainability initiative and hospitality platform for practical,
                                measurable progress
                            </p>
                            <p className="mt-6 text-xs font-bold uppercase tracking-[0.25em] text-[#FDFBF7]/50">
                                Global Green Hotel · 2026
                            </p>
                        </div>
                    </Link>
                    <div aria-hidden="true" className="absolute -bottom-5 -right-5 -z-[1] h-full w-full rounded-2xl border border-[#C2A160]/40" />
                </Reveal>
            </div>
        </section>

        {/* Coming soon library */}
        <section data-testid="research-library" className="grain relative overflow-hidden bg-[#04150D] py-24 sm:py-32">
            <div className="relative z-[2] mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
                <Reveal>
                    <Eyebrow dark>The GGH Knowledge Library</Eyebrow>
                    <h2 className="font-display mt-4 max-w-3xl text-3xl font-medium tracking-tight text-[#FDFBF7] sm:text-4xl">
                        Being built with care — not filled with noise
                    </h2>
                </Reveal>
                <Reveal delay={0.15}>
                    <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#FDFBF7]/70 md:text-lg">
                        GGH is still developing. These areas will be published as credible work
                        becomes available — we do not publish unverified results.
                    </p>
                </Reveal>
                <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="coming-soon-grid">
                    {COMING_SOON.map(({ icon: Icon, title }, i) => (
                        <Reveal key={title} delay={0.06 * i} y={22} className={i === 6 ? "sm:col-span-2 lg:col-span-3" : ""}>
                            <div
                                data-testid={`coming-soon-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                className="flex h-full items-center justify-between rounded-2xl border border-white/10 bg-[#07211A] p-6"
                            >
                                <span className="flex items-center gap-4">
                                    <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#C2A160]/10 text-[#D9B96A]">
                                        <Icon size={19} aria-hidden="true" />
                                    </span>
                                    <span className="font-display text-lg font-medium text-[#FDFBF7]">{title}</span>
                                </span>
                                <span className="rounded-full border border-[#C2A160]/40 px-3 py-1 text-[10px] font-extrabold uppercase tracking-[0.2em] text-[#D9B96A]">
                                    Coming Soon
                                </span>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    </main>
);

export default Research;
